import { LoggingService } from './logging.service';
import { Injectable } from '@angular/core';
import { DataService } from '../pages/offline/data-service';
import { APIService } from './api.service';
import { FileToDelete, FileToUpload } from '../pages/offline/databases/data-db';
import { MediaService } from './media.service';
import { StateService } from './state.service';
import { BehaviorSubject, lastValueFrom, take, tap } from 'rxjs';
import { ContactsService } from '../pages/contacts/contacts.service';
import { ConfigurationService } from '../pages/offline/configuration.service';
import { MapsService } from '../pages/maps/maps.service';
import { ProgramService } from '../pages/programs/program/program.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Program } from '../pages/programs/models/program.model';
import { CurrentProposition } from '../pages/maps/maps-proposition/map-proposition.interface';

@Injectable({
  providedIn: 'root',
})
export class OfflineApiService {
  private readonly syncingSubject = new BehaviorSubject<boolean>(false);
  public syncing = false;
  private readonly populatingLocalCacheSubject = new BehaviorSubject<boolean>(false);
  private readonly populatingLocalCachePercentageSubject = new BehaviorSubject<number>(0);

  private contactIds: Map<string, string> = new Map<string, string>();
  private dynamicFormIds: Map<string, string> = new Map<string, string>();

  public syncing$ = this.syncingSubject.asObservable();
  public populatingLocalCache$ = this.populatingLocalCacheSubject.asObservable();
  public populatingLocalCachePercentage$ = this.populatingLocalCachePercentageSubject.asObservable();
  private isOfflineWarningShown = false;
  private mapPropositionsSyncing = false;
  constructor(
    private readonly logger: LoggingService,
    private readonly apiService: APIService,
    private readonly dataService: DataService,
    private readonly configService: ConfigurationService,
    private readonly mediaService: MediaService,
    private readonly stateService: StateService,
    private readonly contactsService: ContactsService,
    private readonly mapsService: MapsService,
    private readonly programService: ProgramService,
    private readonly msg: NzMessageService,
  ) {
    this.syncing$.pipe(tap((syncing) => (this.syncing = syncing)));
  }

  public async syncOfflineData(): Promise<void> {
    if (this.syncing) {
      return;
    }
    if (!this.apiService.onLine) {
      this.logger.error(new Error('Cannot sync offline data because offline'), false);
      return;
    }
    if (!this.stateService.formDefinitions || this.stateService.formDefinitions.length === 0) {
      this.logger.error(new Error('FormDefinitions not loaded, cannot sync offline data'), false);
      return;
    }
    const storedContactIds = localStorage.getItem('OFFLINE_CONTACT_IDS');
    if (storedContactIds) {
      try {
        this.contactIds = new Map(JSON.parse(storedContactIds));
      } catch (error) {
        localStorage.setItem('OFFLINE_CONTACT_IDS', JSON.stringify([...this.contactIds]));
      }
    }
    const storedDynamicFormIds = localStorage.getItem('OFFLINE_DYNAMIC_FORM_IDS');
    if (storedDynamicFormIds) {
      try {
        this.dynamicFormIds = new Map(JSON.parse(storedDynamicFormIds));
      } catch (error) {
        localStorage.setItem('OFFLINE_DYNAMIC_FORM_IDS', JSON.stringify([...this.dynamicFormIds]));
      }
    }
    this.logger.info('syncing');
    this.syncingSubject.next(true);
    await Promise.all([this.manageContacts(), this.manageMapPropositions()]);
  }

  public async manageMapPropositions(): Promise<void> {
    if (!this.mapPropositionsSyncing) {
      this.mapPropositionsSyncing = true;
      const propositionsToSync = (await this.mapsService.getLocalMapPropositions(true)).filter(
        (proposition) => proposition.inSync === false,
      );

      for (const propositionToSync of propositionsToSync) {
        try {
          await this.mapsService.saveLocalMapProposition(propositionToSync);
          this.createMapPropositionSuccessHistory(propositionToSync);
        } catch (error) {
          this.createMapPropositionFailedHistory(propositionToSync, (error as any).status ?? 500, error as Error);
        }
      }

      this.mapPropositionsSyncing = false;
    }
  }

  private async manageContacts(): Promise<void> {
    await this.removeLocalContactsDoNotExistOnServer().catch((error) => {
      this.logger.error(error, false);
    });
    this.logger.info('manageContacts');
    this.dataService
      .getArchivedContacts()
      .then((archivedContacts) => {
        if (archivedContacts.length === 0) {
          this.logger.info('No offline archived contacts to synchronize');
        }
        for (const archivedContact of archivedContacts) {
          // if this is a new item, we do not want to delete it because it does not exist in the backend
          if (this.apiService.onLine) {
            if (!archivedContact.isNew) {
              this.apiService
                .deleteCollectionItem('contacts', archivedContact._id)
                .then(() => {
                  this.createContactSuccessHistory(archivedContact);
                  this.dataService.deleteLocalContact(archivedContact._id).catch((error) => {
                    this.logger.error(error, false);
                  });
                })
                .catch((error) => {
                  this.logger.error(error, false);
                  this.createContactFailedHistory(archivedContact, error.status ?? 500, error);
                });
            } else {
              this.dataService.deleteLocalContact(archivedContact._id).catch((error) => {
                this.logger.error(error, false);
              });
            }
          } else {
            this.showOfflineWarning();
            this.logger.error(new Error('offline when synchronizing'), false);
          }
        }
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
    this.dataService
      .getNewContacts()
      .then(async (newContacts) => {
        if (newContacts.length === 0) {
          this.logger.info('No offline new contacts to synchronize');
          this.manageDynamicForms().catch((error) => {
            this.logger.error(error, false);
          });
        }
        let index = 0;
        for (const newContact of newContacts) {
          try {
            if (!(await this.tryUploadContactFiles(newContact))) {
              this.logger.error(new Error('Could not upload some files for contact ' + newContact._id), false);
              this.createContactFailedHistory(
                newContact,
                500,
                new Error('Could not upload some files for contact ' + newContact._id),
              );
              index++;
              if (index > newContacts.length - 1) {
                this.manageDynamicForms().catch((error) => {
                  this.logger.error(error, false);
                });
              }
              continue;
            }
            if (this.apiService.onLine) {
              newContact.content.offlineCreated = true;
              newContact.content.offlineCreatedDate = Date.now();
              this.apiService
                .createCollectionItem('contacts', newContact.content)
                .then((contact) => {
                  this.contactIds.set(newContact._id, contact._id);
                  localStorage.setItem('OFFLINE_CONTACT_IDS', JSON.stringify([...this.contactIds]));
                  this.createContactSuccessHistory(newContact);
                  this.dataService
                    .deleteLocalContact(newContact._id)
                    .then(() => {
                      this.dataService.populateContacts([contact]);
                    })
                    .catch((error) => {
                      this.logger.error(error, false);
                    });
                })
                .catch((error) => {
                  this.logger.error(error, false);
                  this.createContactFailedHistory(newContact, error.status ?? 500, error);
                })
                .finally(() => {
                  index++;
                  if (index > newContacts.length - 1) {
                    this.manageDynamicForms().catch((error) => {
                      this.logger.error(error, false);
                    });
                  }
                });
            } else {
              this.showOfflineWarning();
              this.logger.error(new Error('Offline when synchronizing'), false);
              index++;
              if (index > newContacts.length - 1) {
                this.manageDynamicForms().catch((error) => {
                  this.logger.error(error, false);
                });
              }
            }
          } catch (error: any) {
            this.logger.error(error, false);
            this.createContactFailedHistory(newContact, error.status ?? 500, error);
            index++;
            if (index > newContacts.length - 1) {
              await this.manageDynamicForms();
            }
          }
        }
      })
      .catch((error) => {
        this.logger.error(error, false);
        this.manageDynamicForms();
      });
    this.dataService
      .getModifiedContacts()
      .then((modifiedContacts) => {
        if (modifiedContacts.length === 0) {
          this.logger.info('No offline modified contacts to synchronize');
        }
        this.filterModifiedContactsStillExist(modifiedContacts)
          .then(async (filteredModifiedContacts) => {
            for (const modifiedContact of filteredModifiedContacts) {
              try {
                if (!(await this.tryUploadContactFiles(modifiedContact))) {
                  this.logger.error(new Error('Could not upload some files for contact ' + modifiedContact._id), false);
                  this.createContactFailedHistory(
                    modifiedContact,
                    500,
                    new Error('Could not upload some files for contact ' + modifiedContact._id),
                  );
                  continue;
                }

                if (this.apiService.onLine) {
                  modifiedContact.content.offlineModified = true;
                  modifiedContact.content.offlineModifiedDate = Date.now();
                  this.apiService
                    .saveCollectionItem('contacts', modifiedContact.content)
                    .then((contact) => {
                      this.createContactSuccessHistory(modifiedContact);
                      this.dataService
                        .deleteLocalContact(modifiedContact._id)
                        .then(() => {
                          this.dataService.populateContacts([contact]);
                        })
                        .catch((error) => {
                          this.logger.error(error, false);
                        });
                    })
                    .catch((error) => {
                      this.logger.error(error, false);
                      this.createContactFailedHistory(modifiedContact, error.status ?? 500, error);
                    });
                } else {
                  this.showOfflineWarning();
                  this.logger.error(new Error('offline when synchronizing'), false);
                }
              } catch (error: any) {
                this.logger.error(error, false);
                this.createContactFailedHistory(modifiedContact, error.status ?? 500, error);
              }
            }
          })
          .catch((error) => {
            this.logger.error(error, false);
          });
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  private async manageDynamicForms(): Promise<void> {
    this.logger.info('manageDynamicForms');
    await this.removeLocalDynamicFormsDoNotExistOnServer().catch((error) => {
      this.logger.error(error, false);
    });
    this.dataService
      .getArchivedDynamicForms()
      .then((archivedDynamicForms) => {
        if (archivedDynamicForms.length === 0) {
          this.logger.info('No offline archived dynamicForms to synchronize');
        }
        for (const archivedDynamicForm of archivedDynamicForms) {
          // if this is a new item, we do not want to delete it because it does not exist in the backend
          if (!archivedDynamicForm.isNew) {
            if (!archivedDynamicForm.collection || !archivedDynamicForm.definitionId) {
              this.logger.error(
                new Error('Archived local form with no collection or definitionId ' + archivedDynamicForm._id),
                false,
              );
              this.createDynamicFormFailedHistory(
                archivedDynamicForm,
                400,
                new Error('Archived local form with no collection or definitionId'),
              );
            } else {
              if (this.apiService.onLine) {
                if (archivedDynamicForm.collection === 'form-productive-model') {
                  this.apiService
                    .deleteOnlineOfflineProductiveModel(archivedDynamicForm.collection, archivedDynamicForm._id)
                    .then(() => {
                      this.createDynamicFormSuccessHistory(archivedDynamicForm);
                      this.dataService.deleteLocalDynamicForm(archivedDynamicForm._id).catch((error) => {
                        this.logger.error(error, false);
                      });
                    })
                    .catch((error) => {
                      this.logger.error(error, false);
                      this.createDynamicFormFailedHistory(archivedDynamicForm, error.status ?? 500, error);
                    });
                } else {
                  this.apiService
                    .deleteFormItem(archivedDynamicForm.collection, archivedDynamicForm._id)
                    .then(() => {
                      this.createDynamicFormSuccessHistory(archivedDynamicForm);
                      this.dataService.deleteLocalDynamicForm(archivedDynamicForm._id).catch((error) => {
                        this.logger.error(error, false);
                      });
                    })
                    .catch((error) => {
                      this.logger.error(error, false);
                      this.createDynamicFormFailedHistory(archivedDynamicForm, error.status ?? 500, error);
                    });
                }
              } else {
                this.showOfflineWarning();
                this.logger.error(new Error('offline when synchronizing'), false);
              }
            }
          } else {
            this.dataService.deleteLocalDynamicForm(archivedDynamicForm._id).catch((error) => {
              this.logger.error(error, false);
            });
          }
        }
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
    this.dataService
      .getNewDynamicForms()
      .then(async (newDynamicForms) => {
        if (newDynamicForms.length === 0) {
          this.logger.info('No offline new dynamicForms to synchronize');
          this.manageModifiedDynamicForms();
          this.manageProperties().catch((error) => {
            this.logger.error(error, false);
          });
          return;
        }
        const savedDynamicForms: Map<string, any> = new Map<string, any>();
        let index = 0;
        for (const newDynamicForm of newDynamicForms) {
          for (const key of Object.keys(newDynamicForm.content)) {
            if (this.contactIds.get(newDynamicForm.content[key]?._id)) {
              newDynamicForm.content[key]._id = this.contactIds.get(newDynamicForm.content[key]._id);
            }
          }
          try {
            if (!(await this.tryUploadDynamicFormFiles(newDynamicForm))) {
              this.logger.error(new Error('Could not upload some files for form ' + newDynamicForm._id), false);
              this.createDynamicFormFailedHistory(
                newDynamicForm,
                500,
                new Error('Could not upload some files for form ' + newDynamicForm._id),
              );
              index++;
              if (index > newDynamicForms.length - 1) {
                this.manageModifiedDynamicForms();
                this.manageProperties().catch((error) => {
                  this.logger.error(error, false);
                });
              }
              continue;
            }
            if (!newDynamicForm.collection || !newDynamicForm.definitionId) {
              this.logger.error(
                new Error('New local form with no collection or definitionId ' + newDynamicForm._id),
                false,
              );

              this.createDynamicFormFailedHistory(
                newDynamicForm,
                400,
                new Error('New local form with no collection or definitionId'),
              );
              this.dataService.deleteLocalDynamicForm(newDynamicForm._id).catch((error) => {
                this.logger.error(error, false);
              });
              index++;
              if (index > newDynamicForms.length - 1) {
                this.manageModifiedDynamicForms();
                this.manageProperties().catch((error) => {
                  this.logger.error(error, false);
                });
              }
            } else {
              if (this.apiService.onLine) {
                newDynamicForm.content.offlineCreated = true;
                newDynamicForm.content.offlineCreatedDate = Date.now();
                if (newDynamicForm.collection === 'form-productive-model') {
                  const systemInstances = newDynamicForm.content.systemInstances
                    ? newDynamicForm.content.systemInstances
                    : [];
                  delete newDynamicForm.content.systemInstances;
                  this.apiService
                    .createProductiveModel(newDynamicForm.collection, newDynamicForm.content, systemInstances)
                    .then((dynamicForm) => {
                      this.dynamicFormIds.set(newDynamicForm._id, dynamicForm._id);
                      localStorage.setItem('OFFLINE_DYNAMIC_FORM_IDS', JSON.stringify([...this.dynamicFormIds]));
                      savedDynamicForms.set(dynamicForm._id, dynamicForm);
                      this.createDynamicFormSuccessHistory(newDynamicForm);
                      this.dataService
                        .deleteLocalDynamicForm(newDynamicForm._id)
                        .then(() => {
                          this.dataService.populateDynamicForms([dynamicForm], newDynamicForm.collection);
                        })
                        .catch((error) => {
                          this.logger.error(error, false);
                        });
                    })
                    .catch((error) => {
                      this.logger.error(error, false);
                      this.createDynamicFormFailedHistory(newDynamicForm, error.status ?? 500, error);
                    })
                    .finally(() => {
                      index++;
                      if (index > newDynamicForms.length - 1) {
                        for (const savedDynamicForm of savedDynamicForms.values()) {
                          for (const key of Object.keys(savedDynamicForm)) {
                            if (Array.isArray(savedDynamicForm[key])) {
                              for (const arrayItem of savedDynamicForm[key]) {
                                if (
                                  arrayItem?.dynamicFormId?.startsWith('new_') &&
                                  this.dynamicFormIds.get(arrayItem.dynamicFormId)
                                ) {
                                  arrayItem.dynamicFormId = this.dynamicFormIds.get(arrayItem.dynamicFormId);
                                  const collection = this.stateService.getFormDefinitionById(
                                    savedDynamicForm.definitionId,
                                  )?.collectionName;
                                  if (collection) {
                                    this.apiService.saveFormItem(collection, savedDynamicForm).catch((error) => {
                                      this.logger.error(error, false);
                                      // retry another time
                                      this.apiService.saveFormItem(collection, savedDynamicForm).catch((error) => {
                                        this.logger.error(error, false);
                                      });
                                    });
                                  }
                                }
                              }
                            }
                          }
                        }
                        this.manageModifiedDynamicForms();
                        this.manageProperties();
                      }
                    });
                } else {
                  this.apiService
                    .createFormItem(newDynamicForm.collection, newDynamicForm.content)
                    .then((dynamicForm) => {
                      this.dynamicFormIds.set(newDynamicForm._id, dynamicForm._id);
                      localStorage.setItem('OFFLINE_DYNAMIC_FORM_IDS', JSON.stringify([...this.dynamicFormIds]));
                      savedDynamicForms.set(dynamicForm._id, dynamicForm);
                      this.createDynamicFormSuccessHistory(newDynamicForm);
                      this.dataService
                        .deleteLocalDynamicForm(newDynamicForm._id)
                        .then(() => {
                          this.dataService.populateDynamicForms([dynamicForm], newDynamicForm.collection);
                        })
                        .catch((error) => {
                          this.logger.error(error, false);
                        });
                    })
                    .catch((error) => {
                      this.logger.error(error, false);
                      this.createDynamicFormFailedHistory(newDynamicForm, error.status ?? 500, error);
                    })
                    .finally(() => {
                      index++;
                      if (index > newDynamicForms.length - 1) {
                        for (const savedDynamicForm of savedDynamicForms.values()) {
                          for (const key of Object.keys(savedDynamicForm)) {
                            if (Array.isArray(savedDynamicForm[key])) {
                              for (const arrayItem of savedDynamicForm[key]) {
                                if (
                                  arrayItem?.dynamicFormId?.startsWith('new_') &&
                                  this.dynamicFormIds.get(arrayItem.dynamicFormId)
                                ) {
                                  arrayItem.dynamicFormId = this.dynamicFormIds.get(arrayItem.dynamicFormId);
                                  const collection = this.stateService.getFormDefinitionById(
                                    savedDynamicForm.definitionId,
                                  )?.collectionName;
                                  if (collection) {
                                    this.apiService.saveFormItem(collection, savedDynamicForm).catch((error) => {
                                      this.logger.error(error, false);
                                      // retry another time
                                      this.apiService.saveFormItem(collection, savedDynamicForm).catch((error) => {
                                        this.logger.error(error, false);
                                      });
                                    });
                                  }
                                }
                              }
                            }
                          }
                        }
                        this.manageModifiedDynamicForms();
                        this.manageProperties();
                      }
                    });
                }
              } else {
                this.showOfflineWarning();
                this.logger.error(new Error('offline when synchronizing'), false);
              }
            }
          } catch (error: any) {
            this.logger.error(error, false);
            this.createDynamicFormFailedHistory(newDynamicForm, error.status ?? 500, error);
            index++;
            if (index > newDynamicForms.length - 1) {
              if (this.apiService.onLine) {
                for (const savedDynamicForm of savedDynamicForms.values()) {
                  for (const key of Object.keys(savedDynamicForm)) {
                    if (Array.isArray(savedDynamicForm[key])) {
                      for (const arrayItem of savedDynamicForm[key]) {
                        if (
                          arrayItem?.dynamicFormId?.startsWith('new_') &&
                          this.dynamicFormIds.get(arrayItem.dynamicFormId)
                        ) {
                          arrayItem.dynamicFormId = this.dynamicFormIds.get(arrayItem.dynamicFormId);
                          const collection = this.stateService.getFormDefinitionById(savedDynamicForm.definitionId)
                            ?.collectionName;
                          if (collection) {
                            this.apiService.saveFormItem(collection, savedDynamicForm).catch((error) => {
                              this.logger.error(error, false);
                              // retry another time
                              this.apiService.saveFormItem(collection, savedDynamicForm).catch((error) => {
                                this.logger.error(error, false);
                              });
                            });
                          }
                        }
                      }
                    }
                  }
                }
              } else {
                this.showOfflineWarning();
                this.logger.error(new Error('offline when synchronizing'), false);
              }
              this.manageModifiedDynamicForms();
              this.manageProperties().catch((error) => {
                this.logger.error(error, false);
              });
            }
          }
        }
      })
      .catch((error) => {
        this.logger.error(error, false);
        this.manageModifiedDynamicForms();
        this.manageProperties();
      });
  }

  private manageModifiedDynamicForms(): void {
    this.dataService
      .getModifiedDynamicForms()
      .then((modifiedDynamicForms) => {
        if (modifiedDynamicForms.length === 0) {
          this.logger.info('No offline modified dynamicForms to synchronize');
        }
        this.filterModifiedFormsStillExist(modifiedDynamicForms)
          .then(async (filteredModifiedDynamicForms) => {
            for (const modifiedDynamicForm of filteredModifiedDynamicForms) {
              for (const key of Object.keys(modifiedDynamicForm.content)) {
                if (this.contactIds.get(modifiedDynamicForm.content[key]?._id)) {
                  modifiedDynamicForm.content[key]._id = this.contactIds.get(modifiedDynamicForm.content[key]._id);
                }
                if (Array.isArray(modifiedDynamicForm.content[key])) {
                  for (const ref of modifiedDynamicForm.content[key]) {
                    for (const refKey of Object.keys(ref)) {
                      if (this.dynamicFormIds.get(ref[refKey]?.dynamicFormId)) {
                        ref[refKey].dynamicFormId = this.dynamicFormIds.get(ref[refKey].dynamicFormId);
                      }
                    }
                  }
                }
              }

              try {
                if (!(await this.tryUploadDynamicFormFiles(modifiedDynamicForm))) {
                  this.logger.error(
                    new Error('Could not upload some files for form ' + modifiedDynamicForm._id),
                    false,
                  );
                  this.createDynamicFormFailedHistory(
                    modifiedDynamicForm,
                    500,
                    new Error('Could not upload some files for form ' + modifiedDynamicForm._id),
                  );
                  continue;
                }
                if (!modifiedDynamicForm.collection || !modifiedDynamicForm.definitionId) {
                  this.logger.error(
                    new Error('Modified local form with no collection or definitionId ' + modifiedDynamicForm._id),
                    false,
                  );
                  this.createDynamicFormFailedHistory(
                    modifiedDynamicForm,
                    400,
                    new Error('Modified local form with no collection or definitionId'),
                  );
                  this.dataService.deleteLocalDynamicForm(modifiedDynamicForm._id).catch((error) => {
                    this.logger.error(error, false);
                  });
                } else {
                  if (this.apiService.onLine) {
                    modifiedDynamicForm.content.offlineModified = true;
                    modifiedDynamicForm.content.offlineModifiedDate = Date.now();
                    if (modifiedDynamicForm.collection === 'form-productive-model') {
                      const systemInstances = modifiedDynamicForm.content.systemInstances
                        ? modifiedDynamicForm.content.systemInstances
                        : [];
                      delete modifiedDynamicForm.content.systemInstances;
                      this.apiService
                        .saveProductiveModel(
                          modifiedDynamicForm.collection,
                          modifiedDynamicForm.content,
                          systemInstances,
                        )
                        .then((dynamicForm) => {
                          this.createDynamicFormSuccessHistory(modifiedDynamicForm);
                          this.dataService
                            .deleteLocalDynamicForm(modifiedDynamicForm._id)
                            .then(() => {
                              this.dataService.populateDynamicForms([dynamicForm], modifiedDynamicForm.collection);
                            })
                            .catch((error) => {
                              this.logger.error(error, false);
                            });
                        })
                        .catch((error) => {
                          this.logger.error(error, false);
                          this.createDynamicFormFailedHistory(modifiedDynamicForm, error.status ?? 500, error);
                        });
                    } else {
                      this.apiService
                        .saveFormItem(modifiedDynamicForm.collection, modifiedDynamicForm.content)
                        .then((dynamicForm) => {
                          this.createDynamicFormSuccessHistory(modifiedDynamicForm);
                          this.dataService
                            .deleteLocalDynamicForm(modifiedDynamicForm._id)
                            .then(() => {
                              this.dataService.populateDynamicForms([dynamicForm], modifiedDynamicForm.collection);
                            })
                            .catch((error) => {
                              this.logger.error(error, false);
                            });
                        })
                        .catch((error) => {
                          this.logger.error(error, false);
                          this.createDynamicFormFailedHistory(modifiedDynamicForm, error.status ?? 500, error);
                        });
                    }
                  } else {
                    this.showOfflineWarning();
                    this.logger.error(new Error('offline when synchronizing'), false);
                  }
                }
              } catch (error: any) {
                this.logger.error(error, false);
                this.createDynamicFormFailedHistory(modifiedDynamicForm, error.status ?? 500, error);
              }
            }
          })
          .catch((error) => {
            this.logger.error(error, false);
          });
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  private async manageProperties(): Promise<void> {
    await this.removeLocalPropertiesDoNotExistOnServer().catch((error) => {
      this.logger.error(error, false);
    });
    this.dataService
      .getArchivedProperties()
      .then((archivedProperties) => {
        if (archivedProperties.length === 0) {
          this.logger.info('No offline archived properties to synchronize');
        }
        for (const archivedProperty of archivedProperties) {
          // if this is a new item, we do not want to delete it because it does not exist in the backend
          if (this.apiService.onLine) {
            if (!archivedProperty.isNew) {
              this.apiService
                .deleteCollectionItem('properties', archivedProperty._id)
                .then(() => {
                  this.createPropertySuccessHistory(archivedProperty);
                  this.dataService.deleteLocalProperty(archivedProperty._id).catch((error) => {
                    this.logger.error(error, false);
                  });
                })
                .catch((error) => {
                  this.logger.error(error, false);
                  this.createPropertyFailedHistory(archivedProperty, error.status ?? 500, error);
                });
            } else {
              this.dataService.deleteLocalProperty(archivedProperty._id).catch((error) => {
                this.logger.error(error, false);
              });
            }
          } else {
            this.showOfflineWarning();
            this.logger.error(new Error('offline when synchronizing'), false);
          }
        }
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
    this.dataService
      .getNewProperties()
      .then(async (newProperties) => {
        if (newProperties.length === 0) {
          this.logger.info('No offline new properties to synchronize');
        }
        for (const newProperty of newProperties) {
          for (const key of Object.keys(newProperty.content)) {
            if (this.dynamicFormIds.get(newProperty.content[key]?.formInstanceId)) {
              newProperty.content[key].formInstanceId = this.dynamicFormIds.get(
                newProperty.content[key].formInstanceId,
              );
            }
          }
          if (this.apiService.onLine) {
            newProperty.content.offlineCreated = true;
            newProperty.content.offlineCreatedDate = Date.now();

            if (!(await this.tryUploadPropertiesFiles(newProperty))) {
              this.logger.error(new Error('Could not upload some files for form ' + newProperty._id), false);
              this.createPropertyFailedHistory(
                newProperty,
                500,
                new Error('Could not upload some files for property ' + newProperty._id),
              );
              continue;
            }

            this.apiService
              .createCollectionItem('properties', newProperty.content)
              .then((property) => {
                this.createPropertySuccessHistory(newProperty);
                this.dataService
                  .deleteLocalProperty(newProperty._id)
                  .then(() => {
                    this.dataService.populateProperties([property]);
                  })
                  .catch((error) => {
                    this.logger.error(error, false);
                  });
              })
              .catch((error) => {
                this.logger.error(error, false);
                this.createPropertyFailedHistory(newProperty, error.status ?? 500, error);
              });
          } else {
            this.showOfflineWarning();
            this.logger.error(new Error('offline when synchronizing'), false);
          }
        }
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
    this.dataService
      .getModifiedProperties()
      .then((modifiedProperties) => {
        if (modifiedProperties.length === 0) {
          this.logger.info('No offline modified properties to synchronize');
          this.syncingSubject.next(false);
        }
        this.filterModifiedPropertiesStillExist(modifiedProperties)
          .then(async (filteredModifiedProperties) => {
            if (filteredModifiedProperties.length === 0) {
              this.syncingSubject.next(false);
            }
            let index = 0;
            for (const filteredModifiedProperty of filteredModifiedProperties) {
              for (const key of Object.keys(filteredModifiedProperty.content)) {
                if (this.dynamicFormIds.get(filteredModifiedProperty.content[key]?.formInstanceId)) {
                  filteredModifiedProperty.content[key].formInstanceId = this.dynamicFormIds.get(
                    filteredModifiedProperty.content[key].formInstanceId,
                  );
                }
              }
              try {
                if (this.apiService.onLine) {
                  filteredModifiedProperty.content.offlineModified = true;
                  filteredModifiedProperty.content.offlineModifiedDate = Date.now();

                  if (!(await this.tryUploadPropertiesFiles(filteredModifiedProperty))) {
                    this.logger.error(
                      new Error('Could not upload some files for property ' + filteredModifiedProperty._id),
                      false,
                    );
                    this.createPropertyFailedHistory(
                      filteredModifiedProperty,
                      500,
                      new Error('Could not upload some files for property ' + filteredModifiedProperty._id),
                    );
                    continue;
                  }

                  this.apiService
                    .saveCollectionItem('properties', filteredModifiedProperty.content)
                    .then((property) => {
                      this.createPropertySuccessHistory(filteredModifiedProperty);
                      this.dataService
                        .deleteLocalProperty(filteredModifiedProperty._id)
                        .then(() => {
                          this.dataService.populateProperties([property]);
                        })
                        .catch((error) => {
                          this.logger.error(error, false);
                        });
                    })
                    .catch((error) => {
                      this.logger.error(error, false);
                      this.createPropertyFailedHistory(filteredModifiedProperty, error.status ?? 500, error);
                    })
                    .finally(() => {
                      index++;
                      if (index > filteredModifiedProperties.length - 1) {
                        this.syncingSubject.next(false);
                      }
                    });
                } else {
                  this.showOfflineWarning();
                  this.logger.error(new Error('offline when synchronizing'), false);
                  index++;
                  if (index > filteredModifiedProperties.length - 1) {
                    this.syncingSubject.next(false);
                  }
                }
              } catch (error: any) {
                this.logger.error(error, false);
                this.createPropertyFailedHistory(filteredModifiedProperty, error.status ?? 500, error);
                index++;
                if (index > filteredModifiedProperties.length - 1) {
                  this.syncingSubject.next(false);
                }
              }
            }
          })
          .catch((error) => {
            this.logger.error(error, false);
            this.syncingSubject.next(false);
          });
      })
      .catch((error) => {
        this.logger.error(error, false);
        this.syncingSubject.next(false);
      });
  }

  private createDynamicFormSuccessHistory(entry: { content: { _id: string } }): void {
    this.dataService
      .createDynamicFormHistory(entry)
      .then(() => {
        this.logger.info('createDynamicFormSuccessHistory', entry);
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  private createDynamicFormFailedHistory(
    entry: { content: { _id: string; error: Error } },
    returnCode: number,
    error: Error,
  ): void {
    if (error) {
      try {
        entry.content.error = this.serializeError(error);
      } catch (error) {
        this.logger.error(error, false);
      }
    }
    this.dataService
      .createDynamicFormHistory(entry, returnCode)
      .then(() => {
        this.logger.info('createDynamicFormFailedHistory', entry);
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  private createContactFailedHistory(
    entry: { content: { _id: string; firstName: string; lastName: string; descriptor: any; error: Error } },
    returnCode: number,
    error: Error,
  ): void {
    if (error) {
      try {
        entry.content.error = this.serializeError(error);
      } catch (error) {
        this.logger.error(error, false);
      }
    }
    entry.content.descriptor = {
      value: entry.content.firstName + ' ' + entry.content.lastName,
      value_es: entry.content.firstName + ' ' + entry.content.lastName,
      value_fr: entry.content.firstName + ' ' + entry.content.lastName,
    };
    this.dataService
      .createContactHistory(entry, returnCode)
      .then(() => {
        this.logger.info('createContactFailedHistory', entry);
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  private createPropertyFailedHistory(
    entry: { content: { _id: string; name: string; owners: Array<any>; descriptor: any; error: Error } },
    returnCode: number,
    error: Error,
  ): void {
    if (error) {
      try {
        entry.content.error = this.serializeError(error);
      } catch (error) {
        this.logger.error(error, false);
      }
    }
    if (entry.content.name) {
      entry.content.descriptor = {
        value: entry.content.name,
        value_es: entry.content.name,
        value_fr: entry.content.name,
      };
    } else {
      entry.content.descriptor = {
        value:
          entry.content.owners && entry.content.owners.length
            ? entry.content.owners[0].firstName + ' ' + entry.content.owners[0].lastName
            : '',
        value_es:
          entry.content.owners && entry.content.owners.length
            ? entry.content.owners[0].firstName + ' ' + entry.content.owners[0].lastName
            : '',
        value_fr:
          entry.content.owners && entry.content.owners.length
            ? entry.content.owners[0].firstName + ' ' + entry.content.owners[0].lastName
            : '',
      };
    }
    this.dataService
      .createPropertyHistory(entry, returnCode)
      .then(() => {
        this.logger.info('createPropertyFailedHistory', entry);
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  private serializeError(err: unknown): any {
    return JSON.parse(JSON.stringify(err, Object.getOwnPropertyNames(err)));
  }

  private createContactSuccessHistory(entry: {
    content: { _id: string; firstName: string; lastName: string; descriptor: any };
  }): void {
    entry.content.descriptor = {
      value: entry.content.firstName + ' ' + entry.content.lastName,
      value_es: entry.content.firstName + ' ' + entry.content.lastName,
      value_fr: entry.content.firstName + ' ' + entry.content.lastName,
    };
    this.dataService
      .createContactHistory(entry)
      .then(() => {
        this.logger.info('createContactSuccessHistory', entry);
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  private createMapPropositionSuccessHistory(proposition: CurrentProposition): void {
    (proposition as any).descriptor = {
      value: proposition.name,
      value_es: proposition.name,
      value_fr: proposition.name,
    };
    this.dataService
      .createMapPropositionHistory(proposition)
      .then(() => {
        this.logger.info('createMapPropositionSuccessHistory', proposition);
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  private createMapPropositionFailedHistory(proposition: CurrentProposition, returnCode: number, error: Error): void {
    if (error) {
      try {
        (proposition as any).content.error = this.serializeError(error);
      } catch (error) {
        this.logger.error(error, false);
      }
    }
    (proposition as any).descriptor = {
      value: proposition.name,
      value_es: proposition.name,
      value_fr: proposition.name,
    };
    this.dataService
      .createMapPropositionHistory(proposition, returnCode)
      .then(() => {
        this.logger.info('createMapPropositionFailedHistory', proposition);
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  private createPropertySuccessHistory(entry: {
    content: { _id: string; name: string; owners: Array<any>; descriptor: any };
  }): void {
    if (entry.content.name) {
      entry.content.descriptor = {
        value: entry.content.name,
        value_es: entry.content.name,
        value_fr: entry.content.name,
      };
    } else {
      entry.content.descriptor = {
        value:
          entry.content.owners && entry.content.owners.length
            ? entry.content.owners[0].firstName + ' ' + entry.content.owners[0].lastName
            : '',
        value_es:
          entry.content.owners && entry.content.owners.length
            ? entry.content.owners[0].firstName + ' ' + entry.content.owners[0].lastName
            : '',
        value_fr:
          entry.content.owners && entry.content.owners.length
            ? entry.content.owners[0].firstName + ' ' + entry.content.owners[0].lastName
            : '',
      };
    }
    this.dataService
      .createPropertyHistory(entry)
      .then(() => {
        this.logger.info('createPropertySuccessHistory', entry);
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  private async removeLocalDynamicFormsDoNotExistOnServer(): Promise<void> {
    this.dataService
      .getAllDynamicFormEntriesWithoutContent()
      .then((forms) => {
        forms = forms.filter((form) => !form.isNew && form.isArchived === 0);
        try {
          if (forms.length > 0) {
            const definitionIds = [...new Set(forms.map((form) => form.definitionId))];
            for (const formDefId of definitionIds) {
              if (formDefId) {
                const collection =
                  this.stateService.formDefinitions.find((definition) => definition._id === formDefId)
                    ?.collectionName ?? '';
                if (collection) {
                  const ids = forms.filter((form) => form.definitionId === formDefId).map((form) => form._id);
                  if (this.apiService.onLine) {
                    this.apiService
                      .filterExistingItems(collection, ids)
                      .then((validatedIds) => {
                        for (const form of forms) {
                          if (form.definitionId === formDefId && !validatedIds.value.includes(form._id)) {
                            this.dataService.deleteLocalDynamicForm(form._id).catch((error) => {
                              this.logger.error(error, false);
                            });
                          }
                        }
                      })
                      .catch((error) => {
                        this.logger.error(error, false);
                      });
                  } else {
                    this.showOfflineWarning();
                    this.logger.error(new Error('offline when synchronizing'), false);
                  }
                } else {
                  for (const form of forms) {
                    if (form.definitionId === formDefId) {
                      this.dataService.deleteLocalDynamicForm(form._id).catch((error) => {
                        this.logger.error(error, false);
                      });
                    }
                  }
                  this.logger.error(`Collection not found for formDefId ${formDefId}`, false);
                }
              } else {
                this.logger.error(new Error('Form with definitionId undefined'), false);
                for (const form of forms) {
                  if (form.definitionId === formDefId) {
                    this.dataService.deleteLocalDynamicForm(form._id).catch((error) => {
                      this.logger.error(error, false);
                    });
                  }
                }
              }
            }
          }
        } catch (error: any) {
          this.logger.error(error, false);
        }
      })
      .catch((error) => {
        this.logger.error(error, false);
      });
  }

  private async filterModifiedFormsStillExist(modifiedDynamicForms: any[]): Promise<any[]> {
    if (modifiedDynamicForms.length > 0) {
      const definitionIds = [...new Set(modifiedDynamicForms.map((form) => form.definitionId))];

      for (const formDefId of definitionIds) {
        const collection =
          this.stateService.formDefinitions.find((definition) => definition._id === formDefId)?.collectionName ?? '';
        if (collection) {
          if (this.apiService.onLine) {
            const ids = modifiedDynamicForms.filter((form) => form.definitionId === formDefId).map((form) => form._id);
            try {
              const validatedIds = await this.apiService.filterExistingItems(collection, ids);
              modifiedDynamicForms
                .filter((form) => form.definitionId === formDefId && !validatedIds.value.includes(form._id))
                .map((form: any) => (form.itemToBeDeleted = true));
            } catch (error) {
              this.logger.error(error, false);
            }
          } else {
            this.showOfflineWarning();
            this.logger.error(new Error('offline when synchronizing'), false);
          }
        } else {
          this.logger.error(new Error(`collection not found for formDefId ${formDefId}`), false);
        }
      }
    }

    return modifiedDynamicForms.filter((form) => form.itemToBeDeleted !== true);
  }

  private async removeLocalContactsDoNotExistOnServer(): Promise<void> {
    let contacts = await this.dataService.getAllContactEntriesWithoutContent();
    contacts = contacts.filter((contact) => !contact.isNew && contact.isArchived === 0);
    try {
      if (this.apiService.onLine) {
        if (contacts.length > 0) {
          const ids = contacts.map((contact) => contact._id);
          try {
            const validatedIds = await this.apiService.filterExistingItems('contacts', ids);
            for (const contact of contacts) {
              if (!validatedIds.value.includes(contact._id)) {
                this.dataService.deleteLocalContact(contact._id).catch((error) => {
                  this.logger.error(error, false);
                });
              }
            }
          } catch (error) {
            this.logger.error(error, false);
          }
        }
      } else {
        this.showOfflineWarning();
        this.logger.error(new Error('offline when synchronizing'), false);
      }
    } catch (error: any) {
      this.logger.error(error, false);
    }
  }

  private async removeLocalPropertiesDoNotExistOnServer(): Promise<void> {
    let properties = await this.dataService.getAllPropertyEntriesWithoutContent();
    properties = properties.filter((property) => !property.isNew && property.isArchived === 0);
    try {
      if (this.apiService.onLine) {
        if (properties.length > 0) {
          const ids = properties.map((property) => property._id);
          try {
            const validatedIds = await this.apiService.filterExistingItems('properties', ids);
            for (const property of properties) {
              if (!validatedIds.value.includes(property._id)) {
                this.dataService.deleteLocalProperty(property._id).catch((error) => {
                  this.logger.error(error, false);
                });
              }
            }
          } catch (error) {
            this.logger.error(error, false);
          }
        }
      } else {
        this.showOfflineWarning();
        this.logger.error(new Error('offline when synchronizing'), false);
      }
    } catch (error: any) {
      this.logger.error(error, false);
    }
  }

  private async filterModifiedContactsStillExist(modifiedContacts: any[]): Promise<any[]> {
    if (this.apiService.onLine) {
      for (const contact of modifiedContacts) {
        const serverContact = await this.apiService.getItem('contacts', contact._id);
        if (!serverContact) {
          modifiedContacts = modifiedContacts.filter((item) => item._id !== contact._id);
        }
      }
    } else {
      this.showOfflineWarning();
      this.logger.error(new Error('offline when synchronizing'), false);
    }
    return modifiedContacts;
  }

  private async filterModifiedPropertiesStillExist(modifiedProperties: any[]): Promise<any[]> {
    if (this.apiService.onLine) {
      for (const property of modifiedProperties) {
        const serverProperty = await this.apiService.getItem('properties', property._id);
        if (!serverProperty) {
          modifiedProperties = modifiedProperties.filter((item) => item._id !== property._id);
        }
      }
    } else {
      this.showOfflineWarning();
      this.logger.error(new Error('offline when synchronizing'), false);
    }
    return modifiedProperties;
  }

  private async tryUploadContactFiles(obj: any): Promise<boolean> {
    if (!obj.filesToUpload) {
      this.logger.error(new Error('contact.filesToUpload mandatory'), false);
      return true;
    }
    if (obj.filesToUpload.length === 0) {
      return true;
    }
    let retries = 0;
    let runUntilAllOk = true;
    while (runUntilAllOk) {
      try {
        await this.uploadContactFiles(obj);
      } catch (error) {
        this.logger.error(error, false);
      }
      const filesNotUploaded = [];
      for (const fileToUpload of obj.filesToUpload) {
        const fileFromList = obj.content[fileToUpload.controlName].find((file: any) => file.uid === fileToUpload.uid);
        if (!fileFromList || !fileFromList.response || fileFromList.status === 'error') {
          filesNotUploaded.push(fileToUpload);
        }
      }
      if (filesNotUploaded.length) {
        retries++;
        if (retries > 4) {
          return false;
        }
        obj.filesToUpload = [...filesNotUploaded];
      } else {
        runUntilAllOk = false;
      }
    }
    return true;
  }

  private async tryUploadDynamicFormFiles(obj: any): Promise<boolean> {
    if (!obj.filesToUpload) {
      this.logger.error(new Error('dynamicForm.filesToUpload mandatory'), false);
      return true;
    }
    if (obj.filesToUpload.length === 0) {
      return true;
    }
    let retries = 0;
    let runUntilAllOk = true;
    while (runUntilAllOk) {
      try {
        await this.uploadDynamicFormFiles(obj);
      } catch (error) {
        this.logger.error(error, false);
      }
      const filesNotUploaded = [];
      for (const fileToUpload of obj.filesToUpload) {
        const fileFromList = obj.content[fileToUpload.controlName].find((file: any) => file.uid === fileToUpload.uid);
        if (!fileFromList || !fileFromList.response || fileFromList.status === 'error') {
          filesNotUploaded.push(fileToUpload);
        }
      }
      if (filesNotUploaded.length) {
        retries++;
        if (retries > 4) {
          return false;
        }
        obj.filesToUpload = [...filesNotUploaded];
      } else {
        runUntilAllOk = false;
      }
    }
    return true;
  }

  private async tryUploadPropertiesFiles(obj: any): Promise<boolean> {
    if (!obj.filesToUpload) {
      this.logger.error(new Error('property.filesToUpload mandatory'), false);
      return true;
    }
    if (obj.filesToUpload.length === 0) {
      return true;
    }
    let retries = 0;
    let runUntilAllOk = true;
    while (runUntilAllOk) {
      try {
        await this.uploadPropertyFiles(obj);
      } catch (error) {
        this.logger.error(error, false);
      }
      const filesNotUploaded = [];
      for (const fileToUpload of obj.filesToUpload) {
        const fileFromList = obj.content[fileToUpload.controlName].find((file: any) => file.uid === fileToUpload.uid);
        if (!fileFromList || !fileFromList.response || fileFromList.status === 'error') {
          filesNotUploaded.push(fileToUpload);
        }
      }
      if (filesNotUploaded.length) {
        retries++;
        if (retries > 4) {
          return false;
        }
        obj.filesToUpload = [...filesNotUploaded];
      } else {
        runUntilAllOk = false;
      }
    }
    return true;
  }

  private async uploadDynamicFormFiles(obj: any): Promise<void> {
    if (Array.isArray(obj.filesToUpload)) {
      for (const fileToUpload of obj.filesToUpload) {
        if (!this.filesToDeleteContainsFileToUpload(fileToUpload, obj.filesToDelete)) {
          await this.mediaService.uploadNzFile(
            fileToUpload.uid,
            fileToUpload.file,
            fileToUpload.data,
            fileToUpload.s3Path,
            fileToUpload.formDefinitionId,
            obj.content[fileToUpload.controlName],
          );
        }
      }
    }
  }

  private async uploadPropertyFiles(obj: any): Promise<void> {
    if (Array.isArray(obj.filesToUpload)) {
      for (const fileToUpload of obj.filesToUpload) {
        if (!this.filesToDeleteContainsFileToUpload(fileToUpload, obj.filesToDelete)) {
          await this.mediaService.uploadNzFile(
            fileToUpload.uid,
            fileToUpload.file,
            fileToUpload.data,
            fileToUpload.s3Path,
            fileToUpload.formDefinitionId,
            obj.content[fileToUpload.controlName],
          );
        }
      }
    }
  }

  private async uploadContactFiles(obj: any): Promise<void> {
    if (Array.isArray(obj.filesToUpload)) {
      for (const fileToUpload of obj.filesToUpload) {
        if (!this.filesToDeleteContainsFileToUpload(fileToUpload, obj.filesToDelete)) {
          await this.mediaService.uploadNzFile(
            fileToUpload.uid,
            fileToUpload.file,
            fileToUpload.data,
            fileToUpload.s3Path,
            'all',
            obj.content[fileToUpload.controlName],
          );
        }
      }
    }
  }

  public populateLocalCache(): void {
    this.contactsService.currentContact$.pipe(take(1)).subscribe({
      next: (contact) => {
        if (contact) {
          this.logger.info('contact programs', contact.contactPrograms);
          this.populateAll(contact.contactPrograms).catch((error) => {
            this.logger.error(error);
          });
        } else {
          this.logger.error(new Error('current contact not found'));
        }
      },
      error: (error) => {
        this.logger.error(error);
      }
    });
  }

  public clearLocalCache(): void {
    localStorage.removeItem('LOCAL_CACHE_UPDATED');
  }

  private async populateAll(programIds: Array<string>): Promise<void> {
    if (this.syncing) {
      return;
    }
    let lastUpdated = 0;
    if (localStorage.getItem('LOCAL_CACHE_UPDATED')) {
      lastUpdated = Number(localStorage.getItem('LOCAL_CACHE_UPDATED'));
    }
    try {
      this.populatingLocalCacheSubject.next(true);
      this.syncingSubject.next(true);
      this.populatingLocalCachePercentageSubject.next(1);
      await this.removeLocalPropertiesDoNotExistOnServer();
      this.populatingLocalCachePercentageSubject.next(2);
      await this.removeLocalContactsDoNotExistOnServer();
      this.populatingLocalCachePercentageSubject.next(3);
      await this.removeLocalDynamicFormsDoNotExistOnServer();
      this.populatingLocalCachePercentageSubject.next(4);
      await this.populateAllProgramFeatures(programIds, lastUpdated);
      this.populatingLocalCachePercentageSubject.next(15);
      await this.populateAllProperties(programIds, lastUpdated);
      this.populatingLocalCachePercentageSubject.next(25);
      await this.populateAllContacts(lastUpdated);
      this.populatingLocalCachePercentageSubject.next(40);
      await this.populateAllDynamicForms(programIds, lastUpdated);
      this.populatingLocalCachePercentageSubject.next(80);
      await this.populateAllDomVals(lastUpdated);
      this.populatingLocalCachePercentageSubject.next(100);
    } catch (error) {
      this.logger.error(error);
    } finally {
      localStorage.setItem('LOCAL_CACHE_UPDATED', Date.now().toString());
      setTimeout(() => {
        this.populatingLocalCacheSubject.next(false);
        this.syncingSubject.next(false);
      }, 2000);
    }
  }

  private async populateAllProgramFeatures(programIds: Array<string>, lastUpdated: number): Promise<void> {
    const increment = 11 / programIds.length;
    let index = 0;
    await this.mapsService.getFromBaseMap({
      typeNames: ['base_map:program'],
      crs: MapsService.coordinateProjections.web,
    });

    await this.mapsService.getMyPrograms();

    if (!this.mapPropositionsSyncing) {
      await this.mapsService.getLocalMapPropositions(false, true);
    }

    for (const programId of programIds) {
      const program: Program = await lastValueFrom(this.programService.loadProgram(programId));
      if (program.programMapGIS) {
        const programLastUpdated = await this.mapsService.getProgramLastModified(
          program.programMapGIS?.id,
          program.programMapGIS?.geoserverWorkspace,
        );
        if (!programLastUpdated || programLastUpdated.getTime() >= lastUpdated) {
          if (this.apiService.onLine) {
            const geoserverWorkspace = program.programMapGIS?.geoserverWorkspace;
            await this.mapsService.getFromBaseMap({
              typeNames: MapsService.allFeatureNames(geoserverWorkspace),
              programId: program.programMapGIS?.id,
              hideGeometry: true,
              geoserverWorkspace,
            });
            await this.mapsService.getFromBaseMap({
              typeNames: [`${geoserverWorkspace}:program`],
              featureId: program.programMapGIS?.id,
            });
            await this.mapsService.getFromBaseMap({
              typeNames: [`${geoserverWorkspace}:program`],
              crs: MapsService.coordinateProjections.web,
              featureId: program.programMapGIS?.id,
              geoserverWorkspace,
            });
            await this.mapsService.getFromBaseMap({
              typeNames: [`${geoserverWorkspace}:ucs_with_program`],
              crs: MapsService.coordinateProjections.web,
              programId: program.programMapGIS?.id,
              geoserverWorkspace,
            });
            await this.mapsService.getFromBaseMap({
              typeNames: [`${geoserverWorkspace}:property_with_program`],
              crs: MapsService.coordinateProjections.web,
              programId: program.programMapGIS?.id,
              geoserverWorkspace,
            });
            await this.mapsService.getFromBaseMap({
              typeNames: [`${geoserverWorkspace}:zone_with_program`],
              crs: MapsService.coordinateProjections.web,
              programId: program.programMapGIS?.id,
              geoserverWorkspace,
            });
            await this.mapsService.getFromBaseMap({
              typeNames: [`${geoserverWorkspace}:intervention_area_with_program`],
              crs: MapsService.coordinateProjections.web,
              programId: program.programMapGIS?.id,
              geoserverWorkspace,
            });
            await this.mapsService.getFromBaseMap({
              typeNames: [`${geoserverWorkspace}:plot_with_program`],
              crs: MapsService.coordinateProjections.web,
              programId: program.programMapGIS?.id,
              geoserverWorkspace,
            });
            await this.mapsService.getFromBaseMap({
              typeNames: [`${geoserverWorkspace}:reference_plot_with_program`],
              crs: MapsService.coordinateProjections.web,
              programId: program.programMapGIS?.id,
              geoserverWorkspace,
            });
            await this.mapsService.getFromBaseMap({
              typeNames: [`${geoserverWorkspace}:constraint_with_program`],
              crs: MapsService.coordinateProjections.web,
              programId: program.programMapGIS?.id,
              geoserverWorkspace,
            });
            await this.mapsService.getFromBaseMap({
              typeNames: [`${geoserverWorkspace}:station_with_program`],
              crs: MapsService.coordinateProjections.web,
              programId: program.programMapGIS?.id,
              geoserverWorkspace,
            });
            await this.mapsService.getFromBaseMap({
              typeNames: [`${geoserverWorkspace}:property_draft`],
              crs: MapsService.coordinateProjections.web,
              programId: program.programMapGIS?.id,
              geoserverWorkspace,
            });
          } else {
            this.showOfflineWarning();
            this.logger.error(new Error('offline when populating cache'), false);
            this.populatingLocalCachePercentageSubject.next(Math.round(15));
            return;
          }
        }
        this.logger.info('fetching program features ' + index);
      }
      index++;
      this.populatingLocalCachePercentageSubject.next(Math.round(4 + increment * index));
    }
  }

  private async populateAllDynamicForms(programIds: Array<string>, lastUpdated: number): Promise<void> {
    const increment = 40 / this.stateService.formDefinitions.length;
    let index = 0;
    try {
      for (const formDef of this.stateService.formDefinitions) {
        let hasFetchedAll = false;
        let iteration = 0;

        while (!hasFetchedAll) {
          const filters = [];
          if (formDef.programAware) {
            filters.push({
              field: 'programId',
              operator: 'in',
              value: programIds,
            });
          }
          const orFilters = [];
          orFilters.push({
            field: 'created',
            value: lastUpdated,
            operator: 'gte',
          });
          orFilters.push({
            field: 'lastModified',
            value: lastUpdated,
            operator: 'gte',
          });
          filters.push({
            field: 'or',
            value: orFilters,
          });
          if (this.apiService.onLine) {
            const dynForms = await this.apiService.getCollection({
              collection: formDef.collectionName,
              sort: '_id',
              sortOrder: 'asc',
              filters,
              limit: 20,
              skip: 20 * iteration,
              excludeMetadata: false,
            });
            await this.dataService.populateDynamicForms(dynForms, formDef.collectionName);
            if (dynForms.length < 20) {
              hasFetchedAll = true;
            }
            this.logger.info('fetching dynForms ' + formDef.name, 20 * iteration);
            iteration++;
          } else {
            this.showOfflineWarning();
            this.logger.error(new Error('offline when populating cache'), false);
            this.populatingLocalCachePercentageSubject.next(Math.round(80));
            return;
          }
        }
        index++;
        this.populatingLocalCachePercentageSubject.next(Math.round(40 + increment * index));
      }
    } catch (error) {
      this.logger.error(error);
    }
  }

  private showOfflineWarning(): void {
    if (this.isOfflineWarningShown) {
      return;
    }
    this.isOfflineWarningShown = true;
    this.msg.warning(
      $localize`Your network connection seems unstable. The background sync operation may not complete successfully.`,
      {
        nzDuration: 8000,
      },
    );
    setTimeout(() => {
      this.isOfflineWarningShown = false;
    }, 8000);
  }

  private async populateAllContacts(lastUpdated: number): Promise<void> {
    let hasFetchedAll = false;
    let iteration = 0;
    try {
      while (!hasFetchedAll) {
        if (this.apiService.onLine) {
          const filters = [];
          const orFilters = [];
          orFilters.push({
            field: 'created',
            value: lastUpdated,
            operator: 'gte',
          });
          orFilters.push({
            field: 'lastModified',
            value: lastUpdated,
            operator: 'gte',
          });
          filters.push({
            field: 'or',
            value: orFilters,
          });
          const contacts = await this.apiService.getCollection({
            collection: 'contacts',
            sort: '_id',
            sortOrder: 'asc',
            limit: 20,
            filters,
            skip: 20 * iteration,
            excludeMetadata: false,
          });
          await this.dataService.populateContacts(contacts);
          if (contacts.length < 20) {
            hasFetchedAll = true;
          }
          this.logger.info('fetching contacts', 20 * iteration);
          iteration++;
          this.populatingLocalCachePercentageSubject.next(Math.round(25 + iteration <= 40 ? 25 + iteration : 40));
        } else {
          this.showOfflineWarning();
          this.logger.error(new Error('offline when populating cache'), false);
          this.populatingLocalCachePercentageSubject.next(Math.round(40));
          return;
        }
      }
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async populateAllDomVals(lastUpdated: number): Promise<void> {
    let hasFetchedAll = false;
    let iteration = 0;
    try {
      while (!hasFetchedAll) {
        if (this.apiService.onLine) {
          const filters = [];
          const orFilters = [];
          orFilters.push({
            field: 'created',
            value: lastUpdated,
            operator: 'gte',
          });
          orFilters.push({
            field: 'lastModified',
            value: lastUpdated,
            operator: 'gte',
          });
          filters.push({
            field: 'or',
            value: orFilters,
          });
          const domVals = await this.apiService.getCollection({
            collection: 'domain-value',
            sort: '_id',
            sortOrder: 'asc',
            filters,
            limit: 100,
            skip: 100 * iteration,
            excludeMetadata: false,
          });
          await this.configService.populateDomainValue(domVals);
          if (domVals.length < 100) {
            hasFetchedAll = true;
          }
          this.logger.info('fetching domVal', 100 * iteration);
          iteration++;
          this.populatingLocalCachePercentageSubject.next(Math.round(80 + iteration <= 100 ? 80 + iteration : 100));
        } else {
          this.showOfflineWarning();
          this.logger.error(new Error('offline when populating cache'), false);
          this.populatingLocalCachePercentageSubject.next(Math.round(100));
          return;
        }
      }
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async populateAllProperties(programIds: Array<string> = [], lastUpdated: number): Promise<void> {
    const increment = 10 / programIds.length;
    let index = 0;
    try {
      for (const programId of programIds) {
        if (this.apiService.onLine) {
          const filters = [];
          filters.push({
            field: 'programId',
            operator: 'eq',
            value: programId,
          });
          const orFilters = [];
          orFilters.push({
            field: 'created',
            value: lastUpdated,
            operator: 'gte',
          });
          orFilters.push({
            field: 'lastModified',
            value: lastUpdated,
            operator: 'gte',
          });
          filters.push({
            field: 'or',
            value: orFilters,
          });
          let hasFetchedAll = false;
          let iteration = 0;
          while (!hasFetchedAll) {
            const properties = await this.apiService.getCollection({
              collection: 'properties',
              sort: '_id',
              sortOrder: 'asc',
              limit: 20,
              skip: 20 * iteration,
              excludeMetadata: false,
              filters,
            });
            await this.dataService.populateProperties(properties);
            if (properties.length < 20) {
              hasFetchedAll = true;
            }
            this.logger.info('fetching props for program', programId, 20 * iteration);
            iteration++;
          }
          index++;
          this.populatingLocalCachePercentageSubject.next(Math.round(15 + increment * index));
        } else {
          this.showOfflineWarning();
          this.logger.error(new Error('offline when populating cache'), false);
          this.populatingLocalCachePercentageSubject.next(Math.round(25));
          return;
        }
      }
    } catch (error) {
      this.logger.error(error);
    }
  }

  private filesToDeleteContainsFileToUpload(fileToUpload: FileToUpload, filesToDelete: Array<FileToDelete>): boolean {
    if (!filesToDelete || filesToDelete.length === 0) {
      return false;
    }
    for (const fileToDelete of filesToDelete) {
      if (fileToDelete.uid === fileToUpload.uid) {
        return true;
      }
    }
    return false;
  }
}
